import React from 'react';
import { Search } from 'lucide-react';
import styled from '../../styled';
import { useInView, usePrefersReducedMotion } from '../motion';
import {
  AppMockupChrome,
  ChipTone,
  MetricCell,
  MetricChip,
  MockCard,
  ProductMockupBody,
  ProductMockupFrame,
  RankChip,
  SkeletonBar,
  TrendIcon,
  TrendTone
} from './shared';

const ToolbarRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  min-width: 0;
`;

const SearchBox = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 10.5px;
  color: #94a3b8;

  svg {
    flex-shrink: 0;
  }
`;

const FilterChip = styled('span')<{ secondary?: boolean }>`
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 10.5px;
  color: #64748b;
  white-space: nowrap;

  @media (max-width: 460px) {
    display: ${(p) => (p.secondary ? 'none' : 'inline-block')};
  }
`;

const TrendsTable = styled(MockCard)`
  overflow: hidden;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
  }

  th {
    text-align: left;
    font-size: 9.5px;
    font-weight: 500;
    color: #b0b7c3;
    padding: 10px 9px 6px;
    white-space: nowrap;

    &.num {
      text-align: right;
    }
  }

  td {
    padding: 9px;
    color: #334155;
    white-space: nowrap;
    vertical-align: middle;

    &.num {
      text-align: right;
    }

    &.page {
      font-weight: 600;
      color: #0e1c2b;
      font-size: 10.5px;
    }
  }

  @media (max-width: 560px) {
    .earnings-col {
      display: none;
    }
  }

  /* Keep the color-coded CTR chips visible on narrow screens; the
     skeleton metric columns are the ones that can go. */
  @media (max-width: 460px) {
    .pv-col,
    .clicks-col {
      display: none;
    }
  }
`;

const AnimatedRow = styled('tr')<{
  visible: boolean;
  delayMs: number;
  reduced: boolean;
}>`
  border-top: 1px solid #f4f4f2;
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.5s ease ${(p) => p.delayMs}ms,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.delayMs}ms;
`;

type ContentRow = {
  rank: { label: string; tone: 'up' | 'down' | 'flat' };
  page: string;
  pageviews: { w: number; trend: TrendTone };
  clicks: { w: number; trend: TrendTone };
  earnings: { w: number; trend: TrendTone };
  ctr: { value: string; tone: ChipTone };
};

const CONTENT_ROWS: ContentRow[] = [
  {
    rank: { label: '+13', tone: 'up' },
    page: '/best-long-haul-luggage',
    pageviews: { w: 38, trend: 'up' },
    clicks: { w: 36, trend: 'up' },
    earnings: { w: 40, trend: 'up' },
    ctr: { value: '83.2%', tone: 'best' }
  },
  {
    rank: { label: '+1', tone: 'up' },
    page: '/carry-on-luggage-reviews',
    pageviews: { w: 34, trend: 'up' },
    clicks: { w: 32, trend: 'up' },
    earnings: { w: 36, trend: 'up' },
    ctr: { value: '52.5%', tone: 'good' }
  },
  {
    rank: { label: '-1', tone: 'down' },
    page: '/best-travel-backpacks',
    pageviews: { w: 30, trend: 'partial' },
    clicks: { w: 28, trend: 'partial' },
    earnings: { w: 30, trend: 'partial' },
    ctr: { value: '11.5%', tone: 'warn' }
  },
  {
    rank: { label: '+3', tone: 'up' },
    page: '/packing-cubes-guide',
    pageviews: { w: 32, trend: 'up' },
    clicks: { w: 30, trend: 'up' },
    earnings: { w: 34, trend: 'up' },
    ctr: { value: '32.6%', tone: 'ok' }
  },
  {
    rank: { label: '–', tone: 'flat' },
    page: '/best-luggage-brands',
    pageviews: { w: 28, trend: 'flat' },
    clicks: { w: 26, trend: 'flat' },
    earnings: { w: 28, trend: 'flat' },
    ctr: { value: '0%', tone: 'none' }
  },
  {
    rank: { label: '–', tone: 'flat' },
    page: '/weekender-bags-tested',
    pageviews: { w: 26, trend: 'flat' },
    clicks: { w: 26, trend: 'flat' },
    earnings: { w: 26, trend: 'flat' },
    ctr: { value: '0%', tone: 'none' }
  }
];

export const ContentTrendsMockup = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.25 });

  return (
    <ProductMockupFrame ref={ref} aria-hidden>
      <AppMockupChrome product="Optimize" surface="Content Trends" />
      <ProductMockupBody>
        <ToolbarRow>
          <SearchBox>
            <Search size={11} />
            Search pages
          </SearchBox>
          <FilterChip>Last 30 days</FilterChip>
          <FilterChip secondary>vs. previous period</FilterChip>
        </ToolbarRow>

        <TrendsTable>
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Page</th>
                <th className="num pv-col">Pageviews</th>
                <th className="num clicks-col">Clicks</th>
                <th className="num">Page CTR</th>
                <th className="num earnings-col">Earnings</th>
              </tr>
            </thead>
            <tbody>
              {CONTENT_ROWS.map((row, index) => (
                <AnimatedRow
                  key={row.page}
                  visible={inView}
                  delayMs={150 + index * 90}
                  reduced={reduced}
                >
                  <td>
                    <RankChip tone={row.rank.tone}>{row.rank.label}</RankChip>
                  </td>
                  <td className="page">{row.page}</td>
                  <td className="num pv-col">
                    <MetricCell>
                      <SkeletonBar w={row.pageviews.w} h={7} />
                      <TrendIcon tone={row.pageviews.trend} />
                    </MetricCell>
                  </td>
                  <td className="num clicks-col">
                    <MetricCell>
                      <SkeletonBar w={row.clicks.w} h={7} />
                      <TrendIcon tone={row.clicks.trend} />
                    </MetricCell>
                  </td>
                  <td className="num">
                    <MetricChip tone={row.ctr.tone}>{row.ctr.value}</MetricChip>
                  </td>
                  <td className="num earnings-col">
                    <MetricCell>
                      <SkeletonBar w={row.earnings.w} h={7} />
                      <TrendIcon tone={row.earnings.trend} />
                    </MetricCell>
                  </td>
                </AnimatedRow>
              ))}
            </tbody>
          </table>
        </TrendsTable>
      </ProductMockupBody>
    </ProductMockupFrame>
  );
};
